import type { FC } from 'react';
import React, { memo, useCallback } from 'react';
import type { ServiceProviderReceiptBatchRenderRowProps } from './ServiceProviderReceiptBatchReceiptsView';
import { ServiceProviderReceiptBatchRowView } from './ServiceProviderReceiptBatchRowView';
import type { Gender } from '@pflegenavi/shared/api';
import { ImageUploadDropZone } from '@pflegenavi/web-components';
import type { LineItemConfigurationResultDto } from '@pflegenavi/service-provider-shared/api-endpoints';
import { useReceiptBatchStore } from '../ReceiptBatchContext';
import type { TaxItem } from './ServiceProviderReceiptBatchForm';

interface ReceiptBatchRowContainerProps
  extends ServiceProviderReceiptBatchRenderRowProps {
  upload: (file: File) => Promise<string | undefined>;
  residents?: Array<{
    id: string;
    name: string;
    gender: Gender;
  }>;
  archivedResidents?: Array<{
    id: string;
    name: string;
    gender: Gender;
  }>;
  lineItemConfigurations: LineItemConfigurationResultDto[] | undefined;
  autoGeneratedReceipts: boolean;
  isLoadingResidents: boolean;
  enqueueSnackbarOnce: (message: string, options?: any) => void;
  showTabTooltip: boolean;
  setShowTabTooltip: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ServiceProviderReceiptBatchRowContainer: FC<ReceiptBatchRowContainerProps> =
  memo(
    ({
      receipt,
      removeRow,
      index,
      setShownImageId,
      setPreviewImageIds,
      residents,
      archivedResidents,
      isLoadingResidents,
      enqueueSnackbarOnce,
      lastInputRef,
      showTabTooltip,
      setShowTabTooltip,
      upload,
      lineItemConfigurations,
      autoGeneratedReceipts,
    }) => {
      const receiptImageIds = useReceiptBatchStore(
        (state) => state.formik?.values?.receipts[index]?.receiptImageIds
      )!;

      const setValues = useReceiptBatchStore(
        (state) => state.formik?.setValues
      )!;

      const setReceiptFieldValue = useCallback(
        (field: string, value: any) => {
          setValues((prevState) => ({
            ...prevState,
            receipts: prevState.receipts.map((receipt, i) => {
              if (i === index) {
                return {
                  ...receipt,
                  [field]: value,
                };
              }
              return receipt;
            }),
          }));
        },
        [index, setValues]
      );

      const setLineItemFieldValue = useCallback(
        (field: string, lineItemIndex: number, value: any) => {
          setValues((prevState) => ({
            ...prevState,
            receipts: prevState.receipts.map((receipt, i) => {
              if (i === index) {
                return {
                  ...receipt,
                  // eslint-disable-next-line max-nested-callbacks
                  lineItems: receipt.lineItems.map((lineItem, j) => {
                    if (j === lineItemIndex) {
                      return {
                        ...lineItem,
                        [field]: value,
                      };
                    }
                    return lineItem;
                  }),
                };
              }
              return receipt;
            }),
          }));
        },
        [index, setValues]
      );

      const setLineItemValues = useCallback(
        (
          lineItemIndex: number,
          name: string,
          amount: number | undefined,
          tax: TaxItem | undefined
        ) => {
          setValues((prevState) => ({
            ...prevState,
            receipts: prevState.receipts.map((receipt, i) => {
              if (i === index) {
                return {
                  ...receipt,
                  // eslint-disable-next-line max-nested-callbacks
                  lineItems: receipt.lineItems.map((lineItem, j) => {
                    if (j === lineItemIndex) {
                      return {
                        ...lineItem,
                        name,
                        amount,
                        taxes: tax ? [tax] : undefined,
                      };
                    }
                    return lineItem;
                  }),
                };
              }
              return receipt;
            }),
          }));
        },
        [index, setValues]
      );

      const individualDatesMode = useReceiptBatchStore(
        (state) => state.formik?.values.individualDatesMode
      )!;
      const handleSetImages = useCallback(
        (imageIds: string[]) => {
          // We only accept single files at the moment when using drag + drop to be consistent with the file input dialog
          if (imageIds.length !== 1) {
            return;
          }

          if (receiptImageIds && receiptImageIds.length > 0) {
            setReceiptFieldValue('receiptImageIds', [
              ...receiptImageIds,
              ...imageIds,
            ]);
          } else {
            setReceiptFieldValue('receiptImageIds', imageIds);
          }
        },
        [receiptImageIds, setReceiptFieldValue]
      );

      return (
        <ImageUploadDropZone
          upload={upload}
          setImageIds={handleSetImages}
          accept={'image/jpeg,image/png,application/pdf'}
          limit={1}
        >
          <ServiceProviderReceiptBatchRowView
            index={index}
            receipt={receipt}
            individualDatesMode={individualDatesMode}
            setReceiptFieldValue={setReceiptFieldValue}
            setLineItemFieldValue={setLineItemFieldValue}
            setLineItemValues={setLineItemValues}
            removeRow={removeRow}
            setShownImageId={setShownImageId}
            setPreviewImageIds={setPreviewImageIds}
            lastInputRef={lastInputRef}
            showTabTooltip={showTabTooltip}
            setShowTabTooltip={setShowTabTooltip}
            upload={upload}
            addImages={handleSetImages}
            lineItemConfigurations={lineItemConfigurations}
            autoGeneratedReceipts={autoGeneratedReceipts}
            residents={residents}
            archivedResidents={archivedResidents}
            enqueueSnackbarOnce={enqueueSnackbarOnce}
            isLoadingResidents={isLoadingResidents}
          />
        </ImageUploadDropZone>
      );
    }
  );
